import React, { useContext, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { IoIosArrowBack } from "react-icons/io";
import { useToast } from '@chakra-ui/react';
import axiosClient from '../../axios-client';

const chunkLabelToLowercase = (label) => {
  switch (label) {
    case "NUDITÉ": return "nudité";
    case "SCÈNE INTIME": return "scène intime";
    case "VIOLENCE PHYSIQUE": return "violence physique";
    case "VULGARITÉ SONORE": return "vulgarité sonore";
    case "VULGARITÉ VISUELLE": return "vulgarité visuelle";
    default: return label.toLowerCase();
  }
};

function findOverlappingEnd(chunks, currentTime, filters) {
  // Filter the chunks based on the user's filters
  let filteredChunks = chunks.filter(chunk =>
    (chunk.label === "VULGARITÉ SONORE" || chunk.label === "VULGARITÉ VISUELLE") &&
    chunk.start <= currentTime && chunk.end > currentTime &&
    filters[chunk.label]  // Only consider chunks that are active in the filters
  );

  if (filteredChunks.length < 2) return null;

  let soundChunk = filteredChunks.find(chunk => chunk.label === "VULGARITÉ SONORE");
  let visualChunk = filteredChunks.find(chunk => chunk.label === "VULGARITÉ VISUELLE");

  if (!soundChunk || !visualChunk) return null;

  return Math.min(soundChunk.end, visualChunk.end);
}

function PlayVideo(data) {
  const [contentDetails, setContentDetails] = useState();
  const location = useLocation();
  const [videoChunks, setVideoChunks] = useState([]);
  const [filters, setFilters] = useState({});
  const [videoStyle, setVideoStyle] = useState({});
  const videoRef = useRef(null);
  const lastToastRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [watchHistoryId, setWatchHistoryId] = useState(null);
  const [progress, setProgress] = useState(0);
  const [isProgressSet, setIsProgressSet] = useState(false);
  const navigate = useNavigate()
  const toast = useToast()
  useEffect(() => {
    // Ensure the video starts from the correct progress position
    if (videoRef.current && progress > 0) {
      videoRef.current.currentTime = progress;
      setIsLoaded(true);
    }
  }, [progress]);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.preload = "auto";
      setIsLoaded(true);
    }
  }, [contentDetails?.videoUrl]);

  const fetchContentDetails = async (id) => {
    try {
      let response;
      if (data?.type === "episode") {
        response = await axiosClient.get(`/episodes/${id}/`);
      } else if (data?.type === "movie") {
        response = await axiosClient.get(`/movies/${id}/`);
      }
      const contentData = response.data;

      setContentDetails(contentData);
      if (contentData.videoUrl) {
        setIsLoaded(false);
      }
      // Check if a watch history already exists
      
      setVideoChunks(contentData.filter_chunks || []);
    } catch (error) {
      // console.error("Error fetching content details:", error);
    }
  };
  const handleCanPlay = () => {
    if (!isProgressSet && progress > 0 && videoRef.current) {
      videoRef.current.currentTime = progress;
      setIsProgressSet(true);
    }
    setIsLoaded(true);

  };


  useEffect(() => {
    setFilters({
      "NUDITÉ": true,
      "SCÈNE INTIME": true,
      "VIOLENCE PHYSIQUE": true,
      "VULGARITÉ SONORE": true,
      "VULGARITÉ VISUELLE": true
    });

    fetchContentDetails(data?.id);
  }, [data?.id]);


  const handleTimeUpdate = async () => {
    if (!isLoaded) return;
    const currentTime = videoRef.current.currentTime;

    
    const activeChunks = videoChunks.filter(
      chunk => currentTime >= chunk.start && currentTime < chunk.end && filters[chunk.label]
    );
    if (activeChunks.length === 0) {
      setVideoStyle({ filter: "none" });
      videoRef.current.muted = false;
      lastToastRef.current = null;
      return;
    }

    const overlappingEnd = findOverlappingEnd(videoChunks, currentTime, filters);
    if (overlappingEnd) {
      videoRef.current.currentTime = overlappingEnd;
      setVideoStyle({ filter: "none" });
      videoRef.current.muted = false;
      // toast.info("La scène a été sautée car elle contient de la vulgarité sonore et visuelle.");
      toast({
        description: "La scène a été sautée car elle contient de la vulgarité sonore et visuelle.",
        status: 'warning',
        variant: 'left-accent',
        duration: 2000,
        isClosable: true,
        position: 'bottom-right'
      })
      return;
    }

    const skipTypes = ["NUDITÉ", "SCÈNE INTIME", "VIOLENCE PHYSIQUE"];
    const needsSkip = activeChunks.some(chunk => skipTypes.includes(chunk.label));
    const hasSoundVulgarity = activeChunks.some(chunk => chunk.label === "VULGARITÉ SONORE");
    const hasVisualVulgarity = activeChunks.some(chunk => chunk.label === "VULGARITÉ VISUELLE");

    if (needsSkip) {
      const skipChunk = activeChunks.find(chunk => skipTypes.includes(chunk.label));
      videoRef.current.currentTime = skipChunk.end;
    } else if (hasSoundVulgarity) {
      videoRef.current.muted = true;
      setVideoStyle({ filter: "none" });
    } else if (hasVisualVulgarity) {
      videoRef.current.muted = false;
      setVideoStyle({ filter: "brightness(0)" });
    }

    if (lastToastRef.current !== activeChunks.map(chunk => chunk.label).join(',')) {
      lastToastRef.current = activeChunks.map(chunk => chunk.label).join(',');
      const lowercaseLabels = activeChunks.map(chunk => chunkLabelToLowercase(chunk.label));
      const message = lowercaseLabels.length > 2
        ? `${lowercaseLabels.slice(0, -1).join(", ")} et ${lowercaseLabels[lowercaseLabels.length - 1]}`
        : lowercaseLabels.join(" et ");

      let toastMessage = "";
      if (needsSkip) {
        toastMessage = `La scène a été sautée car elle contient ${message}.`;
      } else if (hasSoundVulgarity) {
        toastMessage = `La scène a été mise en sourdine car elle contient ${message}.`;
      } else if (hasVisualVulgarity) {
        toastMessage = `La scène a été assombrie car elle contient ${message}.`;
      }
      toast({
        description: toastMessage,
        status: 'warning',
        variant: 'left-accent',
        duration: 2000,
        isClosable: true,
        position: 'bottom-right'
      })
      // toast.info(toastMessage);
    }
  };


  return (
    <div className="h-screen w-screen flex items-center justify-center bg-black relative ">

      {(!contentDetails?.videoUrl) ? (
        <div className='text-white'>Loading...</div>
      ) : (
        <div >

          <video
            className='h-screen '
            ref={videoRef}
            width="100%"
            // height="100%"
            controls
            autoPlay
            onTimeUpdate={handleTimeUpdate}
            style={videoStyle}
            onCanPlay={handleCanPlay}
            controlsList="nodownload"
          // src={contentDetails.videoUrl}
          // />
          >
            <source src={contentDetails.videoUrl} type="video/mp4" />
            {/* <source src={video} type="video/mp4" /> */}
          </video>

        </div>

      )}
    </div>
  );
}

export default PlayVideo;